import { Component, OnDestroy, OnInit } from '@angular/core';
import { CompanyService } from './company.service';
import { AccountsService } from '../accounts/accounts.service';
import { BayanOnboardingStatus, BayanOnboardingResponse } from '../accounts/accounting-compliance.model';
import { Company, DeliveryEvidencePolicy, SingleInvoiceBilingualPolicy } from './company.model';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SessionStorageService } from '../services/session-storage.service';
import { CustomerService } from '../customer/customer.service';
import { Customer } from '../customer/customer.model';
import { BankAccount } from '../accounts/bank-account/bank-account.model';
import { BankAccountService } from '../accounts/bank-account/bank-account.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BankAccountEntryComponent } from '../accounts/bank-account/bank-account-entry/bank-account-entry.component';
import { EMPTY, Subscription, catchError, finalize, forkJoin, of, switchMap, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FirebaseStorageService } from '../services/firebase-storage.service';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AvatarModule } from 'primeng/avatar';
import { ImageModule } from 'primeng/image';
import { CustomerFormComponent } from 'src/app/customer/customer-form/customer-form.component';
import { TabsModule } from 'primeng/tabs';
import { ToolbarModule } from 'primeng/toolbar';
import { CustomerRolesComponent } from '../customer/customer-roles/customer-roles.component';
import { FieldsetModule } from "primeng/fieldset";
import { ProgressBarModule } from "primeng/progressbar";
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { CompanyFormComponent } from './company-form/company-form.component';
import { OrderControlSettings } from './company.model';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CRM_REGISTRATION_ATTACHMENT_TYPES, CrmClientRegistrationAttachment, CrmRegistrationAttachmentCategory } from '../crm/crm.model';
import { TenantScopeService } from './tenant-scope.service';
import { CompanyGroupAccess, TenantScope } from './tenant-access.model';
import { PermissionService } from '../security/permission.service';


@Component({
    selector: 'app-company',
    templateUrl: './company.component.html',
    styleUrls: ['./company.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        BreadcrumbModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        SelectModule,
        CardModule,
        TagModule,
        ChipModule,
        TabsModule,
        DialogModule,
        ConfirmDialogModule,
        AvatarModule,
        ImageModule,
        CustomerFormComponent,
        CustomerRolesComponent,
        FieldsetModule,
        ProgressBarModule,
        TooltipModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        CompanyFormComponent,
        ToggleSwitchModule
    ],
    providers: [
        CompanyService,
        CustomerService,
        MessageService,
        ConfirmationService,
        BankAccountService,
        DialogService
    ]
})
export class CompanyComponent implements OnInit, OnDestroy {
    private readonly tabKeys = ['employees', 'bank-accounts', 'files', 'settings'] as const;
    private readonly subscriptions = new Subscription();
    private loadingCompanyId?: number;

    company!: Company;
    customers: Customer[] = [];
    bankAccounts: BankAccount[] = [];
    isLoading = true;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    private dialogRef: DynamicDialogRef | undefined;

    // --- State for the static customer form dialog ---
    showCustomerForm = false;
    showCustomerRolesDialog = false;
    customerFormHeader = '';
    customerFormSubmitLabel = '';
    selectedCustomer = new Customer();
    selectedCompany = new Company();
    showCompanyForm = false;
    companyFormHeader = 'Edit Company';
    companyFormSubmitLabel = 'Update';
    employeeSearch = '';
    bankAccountSearch = '';
    isLogoUploadInProgress = false;
    logoUploadProgress = 0;
    companyFiles: CrmClientRegistrationAttachment[] = [];
    companyFileTypes = CRM_REGISTRATION_ATTACHMENT_TYPES;
    activeGroupAccess: CompanyGroupAccess | null = null;
    showCompanyFileDialog = false;
    isCompanyFileUploadInProgress = false;
    companyFileUploadError = '';
    companyFileUploadStage: 'storage' | 'metadata' | null = null;
    private uploadedCompanyFileUrl: string | null = null;
    companyFileDraft: { category: CrmRegistrationAttachmentCategory; file: File | null } = {
        category: 'VAT_CERTIFICATE',
        file: null
    };

    // Bayan Naql Onboarding
    bayanStatus: BayanOnboardingStatus | null = null;
    bayanLoading = false;
    showBayanDialog = false;
    bayanClientId = '';
    orderControlSettings: OrderControlSettings = {};
    readonly accountingMakerCheckerControls: { key: keyof OrderControlSettings; label: string; description?: string }[] = [
        { key: 'enforceInvoiceMakerChecker', label: 'Customer invoice' },
        { key: 'enforceBillMakerChecker', label: 'Vendor bill' },
        { key: 'enforceReceiptMakerChecker', label: 'Receipt' },
        { key: 'enforceVoucherMakerChecker', label: 'Voucher' },
        { key: 'enforcePettyCashMakerChecker', label: 'Petty cash and worker cash',
          description: 'Require a separate finance user for cash-box activation, handoffs, month close, and worker cash settlements.' },
        { key: 'enforceDebitNoteMakerChecker', label: 'Customer debit note' },
        { key: 'enforceCreditNoteMakerChecker', label: 'Vendor credit note' },
        { key: 'enforceCustomerCreditNoteMakerChecker', label: 'Customer credit note', description: 'Applies when the customer credit note workflow is available.' },
        { key: 'enforceVendorDebitNoteMakerChecker', label: 'Vendor debit claim', description: 'Applies when the vendor debit claim workflow is available.' },
        { key: 'enforceJournalEntryMakerChecker', label: 'Journal entry' }
    ];
    orderControlsLoading = false;
    orderControlsSaving = false;
    deliveryEvidencePolicy: DeliveryEvidencePolicy = { enabled: false, maxAccuracyMeters: 500 };
    deliveryEvidencePolicyLoading = false;
    deliveryEvidencePolicySaving = false;
    singleInvoiceBilingualPolicy: SingleInvoiceBilingualPolicy = { required: false };
    singleInvoiceBilingualPolicyLoading = false;
    singleInvoiceBilingualPolicySaving = false;

    constructor(
        private companyService: CompanyService,
        private customerService: CustomerService,
        private messageService: MessageService,
        private bankAccountService: BankAccountService,
        private dialogService: DialogService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private tenantScopeService: TenantScopeService,
        private firebaseStorageService: FirebaseStorageService,
        private accountsService: AccountsService,
        private permissionService: PermissionService
    ) { }
    ngOnDestroy(): void {
        this.dialogRef?.close();
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.route.queryParamMap.subscribe(params => {
            const tab = (params.get('tab') || '').toLowerCase();
            if (tab === 'groups') {
                this.router.navigate(['/company/groups']);
                return;
            }
            this.activeTabIndex = this.resolveTabIndex(tab);
        });
        const companyId = Number(this.route.snapshot.paramMap.get('id')) || this.sessionStorageService.getCompany()?.id;

        if (companyId) {
            this.loadInitialData(companyId);
        } else {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
        }

        this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => {
            this.syncActiveGroupAccess(scope);
            if (scope?.type !== 'COMPANY' || !scope.companyId) {
                return;
            }
            if (scope.companyId === this.company?.id || scope.companyId === this.loadingCompanyId) {
                return;
            }
            this.loadInitialData(scope.companyId);
        }));
    }

    /**
     * Loads all initial data for the component in a more efficient way.
     */
    loadInitialData(companyId: number): void {
        this.isLoading = true;
        this.orderControlsLoading = true;
        this.orderControlSettings = {};
        this.deliveryEvidencePolicyLoading = true;
        this.deliveryEvidencePolicy = { enabled: false, maxAccuracyMeters: 500 };
        this.singleInvoiceBilingualPolicyLoading = true;
        this.singleInvoiceBilingualPolicy = { required: false };
        this.loadingCompanyId = companyId;
        this.companyService.getCompanyById(companyId).pipe(
            switchMap(companyWrapper => {
                this.company = companyWrapper.data;
                if (this.sessionStorageService.getCompany()?.id === this.company.id) {
                    this.sessionStorageService.saveCompany(this.company);
                    this.tenantScopeService.refreshSelectedCompanyDetails(this.company);
                }
                this.syncActiveGroupAccess(this.tenantScopeService.getActiveScope());
                this.breadcrumbItems = [{ label: 'Company' }, { label: this.company.name }];

                // Fetch customers and bank accounts in parallel after getting company info
                return forkJoin({
                    customers: this.customerService.getCustomersByCompany(companyId),
                    bankAccounts: this.bankAccountService.getBankAccounts('Company', companyId, '', 0, 100),
                    companyFiles: this.companyService.getCompanyRegistrationAttachments(companyId),
                    orderControls: this.companyService.getOrderControlSettings(companyId).pipe(catchError(() => of({}))),
                    deliveryEvidencePolicy: this.companyService.getDeliveryEvidencePolicy(companyId).pipe(
                        catchError(() => of({ enabled: false, maxAccuracyMeters: 500 }))
                    ),
                    singleInvoiceBilingualPolicy: this.companyService.getSingleInvoiceBilingualPolicy(companyId).pipe(
                        catchError(() => of({ required: false }))
                    )
                });
            }),
            finalize(() => {
                this.isLoading = false;
                if (this.loadingCompanyId === companyId) {
                    this.loadingCompanyId = undefined;
                    this.orderControlsLoading = false;
                    this.deliveryEvidencePolicyLoading = false;
                    this.singleInvoiceBilingualPolicyLoading = false;
                }
            })
        ).subscribe(({ customers, bankAccounts, companyFiles, orderControls, deliveryEvidencePolicy, singleInvoiceBilingualPolicy }) => {
            // Assign results from parallel calls
            this.bankAccounts = bankAccounts.items;
            this.orderControlSettings = orderControls || {};
            this.deliveryEvidencePolicy = deliveryEvidencePolicy || { enabled: false, maxAccuracyMeters: 500 };
            this.singleInvoiceBilingualPolicy = singleInvoiceBilingualPolicy || { required: false };
            this.customers = customers;
            this.companyFiles = companyFiles;
            // Note: This is an N+1 call pattern. For better performance, consider an endpoint
            // that returns customers with their roles included.
            this.customers.forEach(customer => {
                this.customerService.getRolesByCustomerId(customer.id).subscribe(rolesData => {
                    customer.roles = rolesData;
                });
            });
            this.loadBayanStatus();
        });
    }

    /**
     * Opens the dynamic dialog to create or edit a bank account.
     * This single method replaces the old showNewBankAccountDialog and editBankAccount.
     */
    openBankAccountDialog(account?: BankAccount): void {
        this.dialogRef = this.dialogService.open(BankAccountEntryComponent, {
            header: account ? 'Edit Bank Account' : 'New Bank Account',
            width: '50vw',
            contentStyle: { "overflow": "auto" },
            baseZIndex: 10000,
            modal: true,
            data: {
                ownerType: 'Company',
                ownerId: this.company.id,
                ownerName: this.company.name,
                isDialog: true
            }
        });

        // Subscribe to the onClose event to get data back from the dialog
        this.dialogRef.onClose.subscribe((newOrUpdatedAccount: BankAccount) => {
            if (newOrUpdatedAccount) {
                this.handleBankAccountUpsert(newOrUpdatedAccount);
            }
        });
    }

    private handleBankAccountUpsert(account: BankAccount): void {
        const index = this.bankAccounts.findIndex(acc => acc.id === account.id);

        if (index > -1) {
            // It was an edit, so update the item in the array
            this.bankAccounts[index] = account;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Bank account updated.' });
        } else {
            // It was a new account, so add it to the start of the array
            this.bankAccounts.unshift(account);
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Bank account created.' });
        }
    }

    editCompany(): void {
        this.companyFormHeader = 'Edit Company';
        this.companyFormSubmitLabel = 'Update';
        this.selectedCompany = { ...this.company };
        this.showCompanyForm = true;
    }

    uploadCompanyLogo(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';

        if (!file || !this.company?.id) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid File', detail: 'Please select an image file for the company logo.' });
            return;
        }

        const maxLogoSizeInBytes = 2 * 1024 * 1024;
        if (file.size > maxLogoSizeInBytes) {
            this.messageService.add({ severity: 'warn', summary: 'File Too Large', detail: 'Company logo must be 2 MB or smaller.' });
            return;
        }

        const extension = this.getLogoFileExtension(file);
        const filePath = `companies/${this.company.id}/logo/company-logo.${extension}`;
        this.isLogoUploadInProgress = true;
        this.logoUploadProgress = 0;

        this.firebaseStorageService.uploadFileToExactPathWithProgress(file, filePath).pipe(
            switchMap(progress => {
                this.logoUploadProgress = progress.progress;
                if (!progress.url) {
                    return EMPTY;
                }
                const updatedCompany = { ...this.company, logo: `${progress.url}${progress.url.includes('?') ? '&' : '?'}v=${Date.now()}` };
                return this.companyService.updateCompany(updatedCompany);
            }),
            finalize(() => this.isLogoUploadInProgress = false)
        ).subscribe({
            next: response => {
                if (!response?.data) {
                    return;
                }
                this.company = response.data;
                if (this.sessionStorageService.getCompany()?.id === this.company.id) {
                    this.sessionStorageService.saveCompany(this.company);
                }
                this.messageService.add({ severity: 'success', summary: 'Logo Updated', detail: 'Company logo has been uploaded and saved.' });
            },
            error: error => {
                console.error('Company logo upload failed:', error);
                this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload and save the company logo.' });
            }
        });
    }

    private getLogoFileExtension(file: File): string {
        const extensionFromName = file.name.split('.').pop()?.toLowerCase();
        if (extensionFromName && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(extensionFromName)) {
            return extensionFromName;
        }
        if (file.type === 'image/jpeg') return 'jpg';
        if (file.type === 'image/png') return 'png';
        if (file.type === 'image/webp') return 'webp';
        if (file.type === 'image/gif') return 'gif';
        if (file.type === 'image/svg+xml') return 'svg';
        return 'png';
    }

    openCompanyFileDialog(): void {
        this.companyFileDraft = { category: 'VAT_CERTIFICATE', file: null };
        this.uploadedCompanyFileUrl = null;
        this.companyFileUploadError = '';
        this.showCompanyFileDialog = true;
    }

    onCompanyFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.companyFileDraft.file = input.files?.[0] || null;
        input.value = '';
        this.uploadedCompanyFileUrl = null;
        this.companyFileUploadError = '';
    }

    uploadCompanyFile(): void {
        const file = this.companyFileDraft.file;
        const companyId = this.company?.id;
        if (this.isCompanyFileUploadInProgress) return;
        if (!file || !companyId) {
            this.companyFileUploadError = 'Please select a file to upload.';
            return;
        }

        this.companyFileUploadError = '';
        this.isCompanyFileUploadInProgress = true;
        this.companyFileUploadStage = this.uploadedCompanyFileUrl ? 'metadata' : 'storage';
        const fileUrl$ = this.uploadedCompanyFileUrl
            ? of(this.uploadedCompanyFileUrl)
            : this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `companies/${companyId}/registration-files`).pipe(
                tap(fileUrl => this.uploadedCompanyFileUrl = fileUrl)
            );
        fileUrl$.pipe(
            switchMap(fileUrl => {
                this.companyFileUploadStage = 'metadata';
                return this.companyService.saveCompanyRegistrationAttachment(companyId, {
                    ownerCompanyId: companyId,
                    category: this.companyFileDraft.category,
                    fileName: file.name,
                    fileUrl,
                    fileType: file.type,
                    fileSize: file.size
                });
            }),
            finalize(() => {
                this.isCompanyFileUploadInProgress = false;
                this.companyFileUploadStage = null;
            })
        ).subscribe({
            next: response => {
                if (!response?.success || !response.data) {
                    this.companyFileUploadError = 'File uploaded, but its record could not be saved. Try again.';
                    return;
                }
                this.companyFiles = [response.data, ...this.companyFiles];
                this.showCompanyFileDialog = false;
                this.messageService.add({ severity: 'success', summary: 'File Uploaded', detail: 'Company file has been saved.' });
            },
            error: error => {
                console.error('Company file upload failed:', error);
                this.companyFileUploadError = this.companyFileUploadStage === 'metadata'
                    ? 'File uploaded, but its record could not be saved. Try again.'
                    : 'Could not upload the file. Try again.';
            }
        });
    }

    deleteCompanyFile(file: CrmClientRegistrationAttachment): void {
        if (!this.company?.id || !file.id) {
            return;
        }
        this.confirmationService.confirm({
            message: `Delete "${file.fileName}"?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.companyService.deleteCompanyRegistrationAttachment(this.company.id, file.id!).subscribe({
                    next: () => {
                        this.companyFiles = this.companyFiles.filter(item => item.id !== file.id);
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Company file has been deleted.' });
                    },
                    error: () => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Could not delete the company file.' })
                });
            }
        });
    }

    fileCategoryLabel(category: string): string {
        return this.companyFileTypes.find(type => type.value === category)?.label || category;
    }

    onCompanyFormClosed(): void {
        this.showCompanyForm = false;
        if (this.company?.id) {
            this.loadInitialData(this.company.id);
        }
    }

    /**
     * Prompts for confirmation and deletes a bank account.
     */
    deleteBankAccount(account: BankAccount): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the account "${account.accountName}"?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.bankAccountService.deleteBankAccount(account.id).subscribe({
                    next: () => {
                        this.bankAccounts = this.bankAccounts.filter(acc => acc.id !== account.id);
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Bank account has been deleted.' });
                    },
                    error: (err) => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete bank account.' });
                    }
                });
            }
        });
    }

    // --- Customer management methods (unchanged) ---
    createNewCustomer() {
        this.customerFormHeader = 'Create New Employee';
        this.customerFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer();
        this.selectedCompany = this.company;
        this.showCustomerForm = true;
    }

    editCustomer(customer: Customer) {
        this.customerFormHeader = 'Edit Employee';
        this.customerFormSubmitLabel = 'Update';
        this.selectedCustomer = customer;
        this.selectedCompany = this.company;
        this.showCustomerForm = true;
    }

    onCustomerFormClosed() {
        this.showCustomerForm = false;
        if (this.company?.id) {
            this.loadInitialData(this.company.id);
        }
    }

    deleteCustomer(customer: Customer) {
        // Logic to handle deleting the customer
        console.log('Delete customer:', customer);
    }

    sendEmail(email: string) {
        if (email) {
            window.location.href = `mailto:${email}`;
        } else {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Customer has no email address.' });
        }
    }

    makePhoneCall(phoneNumber: string) {
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        } else {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Customer has no phone number.' });
        }
    }

    manageRoles(customer: Customer) {
        this.selectedCustomer = customer;
        this.showCustomerRolesDialog = true;
    }

    openCompanyGroupsPage(): void {
        this.router.navigate(['/company/groups']);
    }

    saveOrderControls(): void {
        if (!this.company?.id || !this.isCompanyScope || this.orderControlsSaving) {
            return;
        }
        this.orderControlsSaving = true;
        this.companyService.saveOrderControlSettings(this.company.id, this.orderControlSettings).pipe(
            finalize(() => this.orderControlsSaving = false)
        ).subscribe({
            next: settings => {
                this.orderControlSettings = settings;
                this.messageService.add({ severity: 'success', summary: 'Approval controls saved', detail: 'Approval policies were updated for this company.' });
            },
            error: error => {
                this.messageService.add({ severity: 'error', summary: 'Order controls failed', detail: error?.error?.message || 'Unable to save order control settings.' });
            }
        });
    }

    saveDeliveryEvidencePolicy(): void {
        if (!this.company?.id || !this.isCompanyScope || this.deliveryEvidencePolicySaving) {
            return;
        }
        this.deliveryEvidencePolicySaving = true;
        this.companyService.saveDeliveryEvidencePolicy(this.company.id, this.deliveryEvidencePolicy.enabled).pipe(
            finalize(() => this.deliveryEvidencePolicySaving = false)
        ).subscribe({
            next: policy => {
                this.deliveryEvidencePolicy = policy;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Delivery evidence policy saved',
                    detail: policy.enabled
                        ? 'GPS-stamped delivery photos are now required for this company.'
                        : 'GPS-stamped delivery photos are no longer required for this company.'
                });
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Delivery evidence policy failed',
                    detail: error?.error?.message || 'Only company configuration administrators can update this policy.'
                });
            }
        });
    }

    saveSingleInvoiceBilingualPolicy(): void {
        if (!this.company?.id || !this.isCompanyScope || this.singleInvoiceBilingualPolicySaving) {
            return;
        }
        this.singleInvoiceBilingualPolicySaving = true;
        this.companyService.saveSingleInvoiceBilingualPolicy(this.company.id, this.singleInvoiceBilingualPolicy.required).pipe(
            finalize(() => this.singleInvoiceBilingualPolicySaving = false)
        ).subscribe({
            next: policy => {
                this.singleInvoiceBilingualPolicy = policy;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Invoice policy saved',
                    detail: policy.required
                        ? 'Incomplete bilingual data will now block single B2B invoice generation for this company.'
                        : 'Single B2B invoices can now be generated without the bilingual-data requirement.'
                });
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Invoice policy failed',
                    detail: error?.error?.message || 'Only company configuration administrators can update this policy.'
                });
            }
        });
    }

    onCustomerRolesDialogClosed() {
        this.showCustomerRolesDialog = false;
        // Optionally refresh the customer data here
    }

    activeTabIndex: number = 0; // Added activeTabIndex
    onTabChange(event: any) { // Added onTabChange method
        this.activeTabIndex = event.index;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    get visibleCustomers(): Customer[] {
        const text = this.employeeSearch.trim().toLowerCase();
        if (!text) return this.customers;
        return this.customers.filter(customer =>
            `${customer.name || ''} ${customer.emailAddress || ''} ${customer.phoneNumber || ''}`
                .toLowerCase()
                .includes(text)
        );
    }

    get visibleBankAccounts(): BankAccount[] {
        const text = this.bankAccountSearch.trim().toLowerCase();
        if (!text) return this.bankAccounts;
        return this.bankAccounts.filter(account =>
            `${account.bank?.name || ''} ${account.accountName || ''} ${account.accountNumber || ''} ${account.iban || ''} ${account.currencyCode || ''}`
                .toLowerCase()
                .includes(text)
        );
    }

    get isCompanyScope(): boolean {
        return this.tenantScopeService.getActiveScope()?.type === 'COMPANY';
    }

    get canManageCompanyFiles(): boolean {
        return this.isCompanyScope && this.permissionService.hasPermission('company.records.edit');
    }

    get groupMemberCompanies(): Company[] {
        return this.activeGroupAccess?.memberCompanies ?? [];
    }

    get groupCompanyCount(): number {
        return this.groupMemberCompanies.length;
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Employees', value: this.customers.length, hint: 'Linked staff', tone: 'info' },
            { label: 'Bank Accounts', value: this.bankAccounts.length, hint: 'Payment setup', tone: 'success' },
            { label: 'CR Number', value: this.company?.crNumber || '-', hint: 'Commercial registration', tone: 'warn' },
            { label: 'Enterprise', value: this.company?.enableEnterpriseFeatures ? 'Enabled' : 'Disabled', hint: 'Feature access', tone: this.company?.enableEnterpriseFeatures ? 'success' : 'info' }
        ];
    }

    // --- Bayan Naql Onboarding ---

    loadBayanStatus(): void {
        if (!this.company?.id) return;
        this.bayanLoading = true;
        this.accountsService.getBayanOnboardingStatus(this.company.id).pipe(finalize(() => this.bayanLoading = false)).subscribe({
            next: status => this.bayanStatus = status,
            error: () => this.bayanStatus = { onboarded: false }
        });
    }

    openBayanDialog(): void {
        this.bayanClientId = '';
        this.showBayanDialog = true;
    }

    submitBayanOnboarding(): void {
        if (!this.bayanClientId.trim() || !this.company?.id) return;
        this.bayanLoading = true;
        this.accountsService.onboardBayan(this.company.id, this.bayanClientId.trim()).pipe(finalize(() => this.bayanLoading = false)).subscribe({
            next: res => {
                this.messageService.add({ severity: 'success', summary: 'Bayan Onboarded', detail: res.message || 'Bayan credentials saved.' });
                this.showBayanDialog = false;
                this.loadBayanStatus();
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Bayan Onboarding Failed', detail: err.error?.message || err.message });
            }
        });
    }

    deboardBayan(): void {
        if (!this.company?.id) return;
        this.bayanLoading = true;
        this.accountsService.deboardBayan(this.company.id).pipe(finalize(() => this.bayanLoading = false)).subscribe({
            next: res => {
                this.messageService.add({ severity: 'info', summary: 'Bayan Deboarded', detail: res.message || 'Bayan credentials removed.' });
                this.bayanStatus = { onboarded: false };
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Bayan Deboard Failed', detail: err.error?.message || err.message });
            }
        });
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }

    private syncActiveGroupAccess(scope: TenantScope | null): void {
        const groups = this.tenantScopeService.getAccessSummary()?.companyGroups ?? [];

        if (!this.company?.id) {
            this.activeGroupAccess = scope?.type === 'GROUP'
                ? groups.find(group => group.companyGroupId === scope.companyGroupId) ?? null
                : null;
            return;
        }

        if (scope?.type === 'GROUP') {
            const scopedGroup = groups.find(group => group.companyGroupId === scope.companyGroupId);
            if (scopedGroup && (scopedGroup.memberCompanies ?? []).some(company => company.id === this.company.id)) {
                this.activeGroupAccess = scopedGroup;
                return;
            }
        }

        this.activeGroupAccess = groups.find(group =>
            (group.memberCompanies ?? []).some(memberCompany => memberCompany.id === this.company.id)
        ) ?? null;
    }
}
